/**
 * Calibration (CP1) — per-rep self-score vs AI-score gap (manager/admin only).
 *
 * Reads GET /api/manager/calibration over the calls that have BOTH a submitted
 * self-score AND an AI score (the server respects the call-log blocklist for
 * non-admins and excludes calls marked excluded_from_stats). For each rep it
 * shows how their self-grading compares to the AI:
 *
 *   gap = avg_self − avg_ai
 *     gap > 0  → the rep grades themselves HIGHER than the AI  (over-scorer, warm)
 *     gap < 0  → the rep grades themselves LOWER than the AI   (under-scorer, cool)
 *     gap ≈ 0  → well calibrated
 *
 * The route is gated to manager|admin in main.tsx; the server also enforces the
 * role. A date-range filter (from/to) matches the dashboard's window semantics.
 * This is a read-only viewer (no mutations).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { CalibrationFilters, CalibrationRep } from "@/types";
import { PageHeader, Card, Badge, Button } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { IconInfo } from "@/components/icons";

// Quick date presets → concrete local from/to dates (mirrors the dashboard).
type DatePreset = "" | "today" | "7d" | "30d" | "month";
function presetRange(preset: DatePreset): { from: string; to: string } {
  if (!preset) return { from: "", to: "" };
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const now = new Date();
  const today = fmt(now);
  if (preset === "today") return { from: today, to: today };
  if (preset === "month")
    return { from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
  const back = preset === "7d" ? 6 : 29;
  const start = new Date(now);
  start.setDate(start.getDate() - back);
  return { from: fmt(start), to: today };
}

interface DateState {
  from: string; // yyyy-mm-dd local
  to: string;
}

function toServerFilters(d: DateState): CalibrationFilters {
  return {
    ...(d.from ? { from: new Date(`${d.from}T00:00:00`).toISOString() } : {}),
    ...(d.to ? { to: new Date(`${d.to}T23:59:59.999`).toISOString() } : {}),
  };
}

/** A signed gap "+4.2" / "-3.0" / "0.0" (one decimal — these are averages). */
function fmtGap(gap: number): string {
  const rounded = Math.round(gap * 10) / 10;
  if (rounded > 0) return `+${rounded.toFixed(1)}`;
  return rounded.toFixed(1);
}

function fmtAvg(n: number | null): string {
  if (n === null) return "—";
  return (Math.round(n * 10) / 10).toFixed(1);
}

/** Gap text color: over-scorer = warm (amber/red), under-scorer = cool (blue),
 *  well-calibrated = neutral. Matches the contract's over=warm / under=cool. */
function gapColor(rep: CalibrationRep): string {
  if (rep.over_scorer) return "text-band-fair";
  if (rep.under_scorer) return "text-band-solid";
  return "text-muted-foreground";
}

export function Calibration() {
  const [datePreset, setDatePreset] = useState<DatePreset>("");
  const [dates, setDates] = useState<DateState>({ from: "", to: "" });

  const filters = useMemo(() => toServerFilters(dates), [dates]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["manager", "calibration", filters],
    queryFn: ({ signal }) => api.getCalibration(filters, signal),
  });

  function onPreset(p: DatePreset) {
    setDatePreset(p);
    setDates(presetRange(p));
  }

  // Pull the synthetic "overall" row (is_overall) out so we can render it
  // distinctly; the rest are per-rep rows (including the rep_id=null "Unassigned"
  // bucket, which must stay in the table — keyed on is_overall, NOT rep_id===null).
  const reps = data?.reps ?? [];
  const overall = reps.find((r) => r.is_overall) ?? null;
  const repRows = reps.filter((r) => !r.is_overall);

  return (
    <div className="flex flex-1 flex-col">
      <PageHeader
        title="Calibration"
        subtitle="How each rep's self-scores compare to the AI's — who over- or under-rates themselves."
      />

      <div className="flex flex-1 flex-col gap-6 p-5">
        {/* Explainer */}
        <Card className="flex items-start gap-2.5 bg-muted/20">
          <IconInfo className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Gap = self − AI</span>,
            averaged over calls that have both a submitted self-score and an AI
            score.{" "}
            <span className="font-medium text-band-fair">A positive gap</span>{" "}
            means the rep grades themselves higher than the AI does
            (over-scoring);{" "}
            <span className="font-medium text-band-solid">a negative gap</span>{" "}
            means they under-score themselves. A gap near zero is well calibrated.
          </div>
        </Card>

        {/* Date-range filter (consistent with the dashboard). */}
        <Card className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Quick range</span>
            <select
              value={datePreset}
              onChange={(e) => onPreset(e.target.value as DatePreset)}
              className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">All time</option>
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="month">This month</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">From</span>
            <input
              type="date"
              value={dates.from}
              max={dates.to || undefined}
              onChange={(e) => {
                setDatePreset("");
                setDates({ ...dates, from: e.target.value });
              }}
              className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">To</span>
            <input
              type="date"
              value={dates.to}
              min={dates.from || undefined}
              onChange={(e) => {
                setDatePreset("");
                setDates({ ...dates, to: e.target.value });
              }}
              className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
          {(dates.from || dates.to) && (
            <Button
              variant="ghost"
              onClick={() => {
                setDatePreset("");
                setDates({ from: "", to: "" });
              }}
            >
              Clear
            </Button>
          )}
        </Card>

        {isLoading && <LoadingState label="Loading calibration…" />}
        {isError && <ErrorState error={error} onRetry={() => void refetch()} />}

        {!isLoading && !isError && repRows.length === 0 && (
          <EmptyState
            title="Not enough data yet"
            description="Calibration needs calls that have both a submitted self-score and an AI score. Once reps self-score their AI-reviewed calls, their over/under-scoring tendencies appear here."
          />
        )}

        {!isLoading && !isError && repRows.length > 0 && (
          <Card className="overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 font-medium">Rep</th>
                    <th className="px-4 py-2 text-right font-medium">Calls</th>
                    <th className="px-4 py-2 text-right font-medium">Avg self</th>
                    <th className="px-4 py-2 text-right font-medium">Avg AI</th>
                    <th className="px-4 py-2 text-right font-medium">Gap (self − AI)</th>
                    <th className="px-4 py-2 text-right font-medium">Avg mgr score</th>
                    <th className="px-4 py-2 font-medium">Tendency</th>
                  </tr>
                </thead>
                <tbody>
                  {repRows.map((rep) => (
                    <CalibrationRow key={rep.rep_id ?? rep.rep_name} rep={rep} />
                  ))}
                  {overall && (
                    <CalibrationRow rep={overall} isOverall />
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

/** One per-rep (or overall) calibration row. */
function CalibrationRow({
  rep,
  isOverall = false,
}: {
  rep: CalibrationRep;
  isOverall?: boolean;
}) {
  return (
    <tr
      className={
        isOverall
          ? "border-t-2 bg-muted/30 font-medium"
          : "border-b last:border-0 hover:bg-accent/40"
      }
    >
      <td className="px-4 py-2">
        {isOverall ? <span>Overall</span> : rep.rep_name}
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
        {rep.n_calls}
      </td>
      <td className="px-4 py-2 text-right tabular-nums">{fmtAvg(rep.avg_self)}</td>
      <td className="px-4 py-2 text-right tabular-nums">{fmtAvg(rep.avg_ai)}</td>
      <td className={`px-4 py-2 text-right font-semibold tabular-nums ${gapColor(rep)}`}>
        {fmtGap(rep.avg_gap)}
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
        {fmtAvg(rep.avg_override)}
      </td>
      <td className="px-4 py-2">
        {rep.over_scorer ? (
          <Badge tone="warning">Over-scores</Badge>
        ) : rep.under_scorer ? (
          <Badge tone="primary">Under-scores</Badge>
        ) : (
          <Badge tone="success">Calibrated</Badge>
        )}
      </td>
    </tr>
  );
}
